import { ViewportScroller } from '@angular/common';
import { inject, RESPONSE_INIT } from '@angular/core';
import { ResolveFn, Router, UrlTree } from '@angular/router';
import { environment } from '@environments/environment';
import { PageConfiguration, SlugPage } from '@pages/slug-page/models';
import { SlugService } from '@pages/slug-page/services';
import { Time } from '@shared/constants';
import { ConfiguratorLinkService } from '@shared/services/configurator-link';
import { DataLayerService } from '@shared/services/data-layer';
import { DidomiService } from '@shared/services/didomi';
import { CURRENT_LANGUAGE, LANGUAGE_FALLBACK, LanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { PlatformContextService } from '@shared/services/platform-context';
import { ResponsiveService } from '@shared/services/responsive';
import { SeoService } from '@shared/services/seo';
import { extractRegionCode, getUuid } from '@shared/utils';
import { CommonStore, FunnelStore, PageStore } from '@stores';
import { map, of, switchMap, take, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Resolver function that attempts to fetch a SlugPage.
 * Returns an observable of SlugPage, a UrlTree (for redirections), or undefined if an error occurs.
 * This resolver also handles an optional preview mode and replaces empty slugs with 'homepage'.
 */
export const slugPageResolver: ResolveFn<SlugPage | UrlTree | undefined> = (route, state) => {
  const baseUrl = inject(CommonStore).select('baseUrl');
  const seoService = inject(SeoService);
  const pageStore = inject(PageStore);
  const funnelStore = inject(FunnelStore);
  const configuratorLink = inject(ConfiguratorLinkService);
  const platformContextService = inject(PlatformContextService);
  const isBrowser = platformContextService.isBrowser();
  const isServer = platformContextService.isServer();
  const logs = inject(LogsService);
  const languageService = inject(LanguageService);
  const didomiService = inject(DidomiService);
  const dataLayerService = inject(DataLayerService);
  const router = inject(Router);
  const slugService = inject(SlugService);
  const breakpoints = inject(ResponsiveService).breakpoints;
  const viewportScroller = inject(ViewportScroller);
  const applicationType = environment.applicationType;
  if (!funnelStore.read()) {
    funnelStore.set({ validatedStep: 0, currentStep: 1 });
  }
  const urlWithoutQuery: string = state.url.split('?', 1)[0] ?? '';
  const rawSlugPath: string = urlWithoutQuery.startsWith('/') ? urlWithoutQuery.slice(1) : urlWithoutQuery;
  const normalizedPath: string = rawSlugPath.trim().replace(/\/+$/, '');
  const isPreview: boolean = route.queryParamMap.get('preview') === 'true';
  const slug: string = normalizedPath === '' ? 'homepage' : normalizedPath;
  const canonicalLink: string = encodeURI(normalizedPath);

  const defaultApiUrl = new URL(environment.API.endpoint.default).origin;
  seoService.setPreconnectLink(defaultApiUrl);
  const defaultElasticSearchUrl = new URL(environment.API.endpoint.elasticsearch).origin;
  seoService.setPreconnectLink(defaultElasticSearchUrl);

  const responseInit = inject(RESPONSE_INIT, { optional: true });

  const CHECKOUT_STEPS_WITH_STORE_NAME = new Set<number>([2, 3, 4]);

  logs.trace('slugPageResolver', `Slug is "${slug}"`, isPreview ? 'on preview mode' : '');

  const updateSeoForSlugPage = (config: PageConfiguration): void => {
    const seo = config.seo;
    const title = seo.title;
    const description = seo.description;
    const twitter = {
      title,
      description,
      ...seo.twitter,
    };
    const og = {
      title,
      description,
      ...(seo?.url !== undefined && { url: seo.url }),
      ...seo.og,
    };
    seoService.updateTitle(title);
    seoService.updateMetaDescription(description);
    seoService.setCanonicalLink(canonicalLink);
    seoService.setTwitterCard(twitter);
    seoService.setOpenGraph(og);
  };

  const emitGtmEvent = (pageData: SlugPage): void => {
    const country = extractRegionCode(CURRENT_LANGUAGE());
    const gtmConfig = pageData.config.seo.gtmConfig;

    let checkout_store_front = '';
    const checkout_step = gtmConfig?.checkout_step ? Number(gtmConfig?.checkout_step) : undefined;
    if (checkout_step && CHECKOUT_STEPS_WITH_STORE_NAME.has(checkout_step)) {
      checkout_store_front = funnelStore.selectPath((state) => state.storeSelected?.name)() ?? '';
    }

    const event = {
      event: gtmConfig?.event || 'screen_view',
      env_country: country,
      env_work: applicationType,
      env_channel: breakpoints(),
      page_name: slug === 'homepage' ? '/' : `/${slug}`,
      page_type: gtmConfig?.page_type || undefined,
      page_cat1_name: gtmConfig?.page_cat1_name || undefined,
      page_cat2_name: gtmConfig?.page_cat2_name || undefined,
      page_cat3_name: gtmConfig?.page_cat3_name || undefined,
      checkout_step: checkout_step || undefined,
      checkout_type: gtmConfig?.checkout_type || undefined,
      checkout_store: gtmConfig?.checkout_store || checkout_store_front || undefined,
      tunnel_referrer: gtmConfig?.tunnel_referrer || undefined,
      group: gtmConfig?.group || undefined,
      description: gtmConfig?.description || undefined,
      context: gtmConfig?.context || undefined,
      event_type: 'view',
    } as const;
    dataLayerService.push(event);
  };

  let request = slugService.readSlug(slug, isPreview ? { preview: 'true' } : {}, {
    cacheExpirationTime: Time.TEN_MINUTES,
  });

  if (environment.isPrefetchCmsEnabled) {
    request = slugService.readPrefetch(slug, { cacheExpirationTime: 600 });
  }

  return request.pipe(
    take(1),
    map((pageData) => {
      pageData['blocks'] = pageData.blocks.map((block) => ({ ...block, _uuid: getUuid() }));
      return pageData;
    }),
    switchMap((pageData: SlugPage) => {
      const language = pageData.config.language ?? LANGUAGE_FALLBACK;

      if (!pageData.config.language) {
        logs.error('slugPageResolver', 'LANGUAGE_FALLBACK used: no language defined in page configuration', {
          slug,
          fallback: LANGUAGE_FALLBACK,
        });
      }

      return languageService.use(language).pipe(
        switchMap(() => didomiService.init()),
        tap(() => {
          logs.trace('slugPageResolver', 'Slug data:', pageData);
          emitGtmEvent(pageData);
          if (isBrowser) {
            viewportScroller.scrollToPosition([0, 0]);
          }
          updateSeoForSlugPage(pageData.config);
          pageData.url = slug;
          pageStore.set(pageData);
          configuratorLink.clearIfLeftFunnel(pageData.type);
          if (pageData.type === 'page_404') {
            if (isServer) {
              if (responseInit) {
                responseInit.status = 404;
              }
            }
          }
        }),
        map((): SlugPage => pageData)
      );
    }),
    catchError((error: unknown) => {
      logs.fatal('slugPageResolver', 'Failed to resolve page', { error, normalizedPath });
      if (normalizedPath !== '') {
        void router.navigate([baseUrl() ?? '']);
      }
      return of(undefined);
    })
  );
};
